import { Controller } from "@hotwired/stimulus"

// The emoji field (shared/_emoji_picker). The picker opens as a lifted sheet
// fixed to the page under the tile (or above it when there is no room), so a
// form sheet that clips its overflow never cuts it off. Scrolling, resizing,
// Escape or a click outside closes it.
const GAP = 6
const MARGIN = 8

// emoji-picker-element draws its search field and tabs inside a shadow root,
// where the page's styles cannot reach. Its theme variables cover the colours;
// these rules give the search field the house field look.
const PICKER_STYLE = `
  input.search {
    background: var(--field-fill);
    border: 1px solid var(--field-edge);
    border-radius: var(--field-radius, 6px);
    color: var(--ink, #342d26);
  }
  input.search::placeholder { color: var(--field-hint); opacity: 1; }
  input.search:hover { border-color: var(--field-edge-hover); }
  input.search:focus { background: var(--field-focus-fill); border-color: var(--field-focus-ink); box-shadow: var(--field-focus-ring); outline: none; }
`

export default class extends Controller {
  static targets = ["button", "popup", "input", "glyph"]

  connect() {
    this.onDocumentClick = (event) => {
      if (!this.popupTarget.contains(event.target) && !this.buttonTarget.contains(event.target)) this.close()
    }
    this.onKeydown = (event) => { if (event.key === "Escape") this.close({ refocus: true }) }
    this.onViewportChange = (event) => {
      // A scroll inside the picker's own emoji grid is not a page scroll.
      if (event.type === "scroll" && this.popupTarget.contains(event.target)) return
      this.close()
    }

    const picker = this.picker
    if (picker) {
      picker.addEventListener("emoji-click", (event) => this.selectEmoji(event.detail.unicode))
      this.stylePicker(picker)
    }
    document.addEventListener("click", this.onDocumentClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick)
    this.stopWatching()
  }

  get picker() {
    return this.popupTarget.querySelector("emoji-picker")
  }

  // The element upgrades once its module loads; style it as soon as it has a
  // shadow root.
  stylePicker(picker, tries = 40) {
    if (picker.shadowRoot) {
      if (picker.shadowRoot.querySelector("style[data-house]")) return
      const style = document.createElement("style")
      style.dataset.house = ""
      style.textContent = PICKER_STYLE
      picker.shadowRoot.appendChild(style)
    } else if (tries > 0) {
      setTimeout(() => this.stylePicker(picker, tries - 1), 100)
    }
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.popupTarget.hidden ? this.open() : this.close()
  }

  open() {
    this.popupTarget.hidden = false
    this.buttonTarget.setAttribute("aria-expanded", "true")
    this.place()
    this.watch()
    const search = this.picker?.shadowRoot?.querySelector("input.search")
    if (search) search.focus({ preventScroll: true })
  }

  close({ refocus = false } = {}) {
    if (this.popupTarget.hidden) return
    this.popupTarget.hidden = true
    this.buttonTarget.setAttribute("aria-expanded", "false")
    this.stopWatching()
    if (refocus) this.buttonTarget.focus()
  }

  // Under the tile, left edges aligned; above it when the space below is
  // short; always inside the viewport.
  place() {
    const panel = this.popupTarget
    const tile = this.buttonTarget.getBoundingClientRect()
    const { width, height } = panel.getBoundingClientRect()
    const below = tile.bottom + GAP
    const top = below + height > window.innerHeight - MARGIN && tile.top - GAP - height >= MARGIN
      ? tile.top - GAP - height
      : Math.max(MARGIN, Math.min(below, window.innerHeight - MARGIN - height))
    const left = Math.max(MARGIN, Math.min(tile.left, window.innerWidth - MARGIN - width))
    panel.style.top = `${Math.round(top)}px`
    panel.style.left = `${Math.round(left)}px`
  }

  watch() {
    document.addEventListener("keydown", this.onKeydown)
    window.addEventListener("resize", this.onViewportChange)
    window.addEventListener("scroll", this.onViewportChange, true)
  }

  stopWatching() {
    document.removeEventListener("keydown", this.onKeydown)
    window.removeEventListener("resize", this.onViewportChange)
    window.removeEventListener("scroll", this.onViewportChange, true)
  }

  selectEmoji(unicode) {
    this.glyphTarget.textContent = unicode
    this.inputTarget.value = unicode
    this.close({ refocus: true })
  }

  clear(event) {
    event.preventDefault()
    event.stopPropagation()
    this.glyphTarget.textContent = ""
    this.inputTarget.value = ""
    this.close({ refocus: true })
  }
}
